import { RollOperation } from "@/pages/rollshop/models/Operation";
import { upsertRollOperation } from "@/pages/rollshop/repositories/OperationRepository";
import { CommentsField } from "@/shared/components/CommentsField";
import { OperatorSelector } from "@/shared/selectors/OperatorSelector";
import {
	ErrorModal,
	getMomentTz,
	MesfModal,
	TimeService,
} from "@dexteel/mesf-core";
import { Button, Grid2 } from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";

const moment = getMomentTz();

type Model = {
	Comments: string;
	OperatorId: number | null;
	Timestamp: Date;
};

type Props = {
	action: "Cutting" | "Dressing" | "Spray Welding" | "Cleaning" | "";
	roll: RollOperation | null;
	show: boolean;
	onHide: (updated: boolean) => void;
};

export const ConfirmActionModal = ({ action, roll, show, onHide }: Props) => {
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [error, setError] = useState("");

	const rollOperationTypeCode =
		action == "Cutting"
			? "C"
			: action == "Dressing"
				? "S"
				: action == "Spray Welding"
					? "W"
					: "L";

	const { control, reset, handleSubmit, getValues } = useForm<Model>({
		mode: "onChange",
		defaultValues: {
			Comments: ``,
			OperatorId: null,
			Timestamp: new Date(),
		},
	});

	const upsertMutation = useMutation(upsertRollOperation, {
		onSuccess: async () => {
			onHide(true);
			reset();
		},
		onError: (error: Error) => {
			setError(error.message);
		},
		onSettled: () => setIsSubmitLoading(false),
	});

	const onSubmit: SubmitHandler<Model> = (data) => {
		setIsSubmitLoading(true);
		upsertMutation.mutate({
			partId: roll?.PartId!,
			timestamp: data.Timestamp,
			operatorId: data.OperatorId,
			rollOperationTypeCode,
			comments: data.Comments,
		});
	};

	useEffect(() => {
		reset({
			Comments: "",
			OperatorId: null,
			Timestamp: new Date(),
		});
	}, [roll, action]);

	return (
		<>
			<MesfModal
				title={`Operation: ${action}`}
				open={show}
				id="confirm-modal"
				handleClose={() => onHide(true)}
			>
				<form onSubmit={handleSubmit(onSubmit)}>
					<MesfModal.Content dividers style={{ padding: "15px 30px" }}>
						<Grid2 container spacing={1}>
							<Grid2 size={{ md: 12, xs: 12 }}>
								<Controller
									name="Comments"
									control={control}
									render={({
										field: { value, onChange },
										fieldState: { error },
									}) => (
										<CommentsField
											value={value}
											onChange={onChange}
											fieldError={error}
											getValues={getValues}
											maxLength={400}
											rows={{ minRows: 4, maxRows: 4 }}
										/>
									)}
								/>
							</Grid2>
							<Grid2 size={{ xs: 12, md: 6 }}>
								<Controller
									name="OperatorId"
									control={control}
									render={({
										field: { value, onChange },
										fieldState: { error },
									}) => (
										<OperatorSelector
											label="Operator"
											value={value}
											onChange={onChange}
											fieldError={error}
										/>
									)}
								/>
							</Grid2>
							<Grid2 size={{ xs: 12, md: 6 }}>
								<Controller
									name="Timestamp"
									control={control}
									rules={{
										required: "Timestamp date is required",
										validate: {
											notInFuture: (value) => {
												if (value && value > new Date()) {
													return "Timestamp date cannot be in the future.";
												}
												return true;
											},
											isWithinLastYear: (value) => {
												const today = new Date();
												const oneYearAgo = new Date();
												oneYearAgo.setFullYear(today.getFullYear() - 1);

												if (value && (value < oneYearAgo || value > today)) {
													return "Timestamp date cannot exceed one year.";
												}
												return true;
											},
										},
									}}
									render={({
										field: { value, onChange },
										fieldState: { error },
									}) => (
										<DateTimePicker
											label="Timestamp"
											value={value ? moment(value) : null}
											onChange={onChange}
											timezone={TimeService.getInstance().getServerTimeZone()}
											format="MM/DD/YYYY HH:mm:ss"
											ampm={false}
											slotProps={{
												textField: {
													size: "small",
													error: !!error,
													helperText: error?.message,
													fullWidth: true,
												},
											}}
										/>
									)}
								/>
							</Grid2>
						</Grid2>
					</MesfModal.Content>
					<MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
						<Grid2 container spacing={2} justifyContent="flex-end">
							<Grid2 size={{ md: 3, xs: 12 }} style={{ margin: 0 }}>
								<Button
									fullWidth
									variant="contained"
									color="inherit"
									onClick={() => onHide(false)}
									disabled={isSubmitLoading}
								>
									Cancel
								</Button>
							</Grid2>
							<Grid2 size={{ xs: 12, md: 4 }} style={{ margin: 0 }}>
								<Button
									fullWidth
									variant="contained"
									color="primary"
									type="button"
									onClick={handleSubmit(onSubmit)}
									disabled={isSubmitLoading}
									data-test-id="confirm-button"
								>
									Confirm
								</Button>
							</Grid2>
						</Grid2>
					</MesfModal.Actions>
				</form>
			</MesfModal>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
